import { comparePositioned } from './position';
import { FALLBACK_PARENT, ROOT_KEYS, isRootId, rootId, type BookmarkRow } from './types';

/**
 * The visible bookmark tree derived from server rows. This function is the
 * single definition of "what the tree looks like" and is shared by every
 * client, so all replicas render identical trees from identical rows.
 *
 * Rules (all deterministic):
 *  - Tombstoned nodes are hidden.
 *  - A live node whose parent is tombstoned is shown under the nearest live
 *    ancestor ("rescue"), so concurrent additions into a deleted folder are
 *    never silently lost.
 *  - A node whose parent chain is broken (unknown id, non-folder parent) is
 *    shown under Other bookmarks.
 *  - If a cycle ever appears (only possible with optimistic local overlays;
 *    the server rejects cyclic moves), the member with the greatest
 *    (locationHlc, id) is detached to Other bookmarks.
 *  - Siblings are ordered by (position, id).
 */
export interface MaterializedTree {
  rows: ReadonlyMap<string, BookmarkRow>;
  /** Effective parent of every live node. */
  parentOf: Map<string, string>;
  /** Ordered live children of every folder / root (only non-empty entries). */
  childrenOf: Map<string, string[]>;
}

export function materialize(input: Iterable<BookmarkRow> | ReadonlyMap<string, BookmarkRow>): MaterializedTree {
  const rows: ReadonlyMap<string, BookmarkRow> =
    input instanceof Map ? input : new Map([...(input as Iterable<BookmarkRow>)].map((r) => [r.id, r]));
  const validRoots = new Set(ROOT_KEYS.map(rootId));
  const parentOf = new Map<string, string>();

  const resolveParent = (row: BookmarkRow): string => {
    let p = row.parentId;
    const seen = new Set<string>([row.id]);
    for (;;) {
      if (isRootId(p)) return validRoots.has(p) ? p : FALLBACK_PARENT;
      if (seen.has(p)) return FALLBACK_PARENT;
      seen.add(p);
      const parent = rows.get(p);
      if (!parent || parent.kind !== 'folder') return FALLBACK_PARENT;
      if (!parent.deleted) return p;
      p = parent.parentId;
    }
  };

  for (const row of rows.values()) {
    if (!row.deleted) parentOf.set(row.id, resolveParent(row));
  }

  breakCycles(parentOf, rows);

  const childrenOf = new Map<string, string[]>();
  for (const [id, parent] of parentOf) {
    let list = childrenOf.get(parent);
    if (!list) childrenOf.set(parent, (list = []));
    list.push(id);
  }
  for (const list of childrenOf.values()) {
    list.sort((a, b) => comparePositioned(rows.get(a)!, rows.get(b)!));
  }
  return { rows, parentOf, childrenOf };
}

function breakCycles(parentOf: Map<string, string>, rows: ReadonlyMap<string, BookmarkRow>) {
  const state = new Map<string, 1 | 2>(); // 1 = on current path, 2 = known to reach a root
  for (const start of parentOf.keys()) {
    if (state.get(start) === 2) continue;
    const path: string[] = [];
    let cur: string | undefined = start;
    while (cur && !isRootId(cur) && state.get(cur) !== 2) {
      if (state.get(cur) === 1) {
        const cycle = path.slice(path.indexOf(cur));
        let victim = cycle[0]!;
        for (const id of cycle) {
          const a = rows.get(id)!;
          const v = rows.get(victim)!;
          if (a.locationHlc > v.locationHlc || (a.locationHlc === v.locationHlc && id > victim)) victim = id;
        }
        parentOf.set(victim, FALLBACK_PARENT);
        break;
      }
      state.set(cur, 1);
      path.push(cur);
      cur = parentOf.get(cur);
    }
    for (const id of path) state.set(id, 2);
  }
}

/** Ancestors of a live node from its parent up to (and including) the root. */
export function ancestorsOf(tree: MaterializedTree, id: string): string[] {
  const out: string[] = [];
  let p = tree.parentOf.get(id);
  while (p) {
    out.push(p);
    if (isRootId(p)) break;
    p = tree.parentOf.get(p);
  }
  return out;
}

export function isDescendant(tree: MaterializedTree, id: string, maybeAncestor: string): boolean {
  return ancestorsOf(tree, id).includes(maybeAncestor);
}

export function descendantsOf(tree: MaterializedTree, id: string): string[] {
  const out: string[] = [];
  const stack = [...(tree.childrenOf.get(id) ?? [])];
  while (stack.length) {
    const cur = stack.pop()!;
    out.push(cur);
    stack.push(...(tree.childrenOf.get(cur) ?? []));
  }
  return out;
}

export function countDescendants(tree: MaterializedTree, id: string): { folders: number; bookmarks: number } {
  let folders = 0;
  let bookmarks = 0;
  for (const d of descendantsOf(tree, id)) {
    const k = tree.rows.get(d)?.kind;
    if (k === 'folder') folders++;
    else if (k === 'bookmark') bookmarks++;
  }
  return { folders, bookmarks };
}
